/**
 * SpotlightOverlay
 *
 * Renders four dark overlay divs that surround the highlighted target area,
 * creating a "spotlight" cutout effect.  Each div has a CSS transition so the
 * highlight smoothly follows the element when steps change.
 *
 * For modal-type steps (no target), a single full-screen overlay is rendered.
 */

import React from 'react';

export type SpotlightRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type SpotlightOverlayProps = {
  rect: SpotlightRect | null;
  isModal: boolean;
};

const OVERLAY_COLOR = 'rgba(0, 0, 0, 0.72)';
const TRANSITION = 'all 0.3s ease';

export const SpotlightOverlay = ({ rect, isModal }: SpotlightOverlayProps) => {
  const base: React.CSSProperties = {
    position: 'fixed',
    backgroundColor: OVERLAY_COLOR,
    zIndex: 9998,
    transition: TRANSITION,
    // Block pointer events on the overlay so background elements are not clickable.
    pointerEvents: 'all',
  };

  // Modal steps or unfound targets → single full-screen dim.
  if (isModal || !rect) {
    return <div style={{ ...base, inset: 0 }} />;
  }

  const { x, y, width, height } = rect;

  return (
    <>
      {/* top strip */}
      <div style={{ ...base, top: 0, left: 0, right: 0, height: y }} />
      {/* bottom strip */}
      <div style={{ ...base, top: y + height, left: 0, right: 0, bottom: 0 }} />
      {/* left strip (only at target height) */}
      <div style={{ ...base, top: y, left: 0, width: x, height }} />
      {/* right strip (only at target height) */}
      <div style={{ ...base, top: y, left: x + width, right: 0, height }} />

      {/* highlight ring — sits above the overlay, pointer-events off so target stays clickable */}
      <div
        style={{
          position: 'fixed',
          top: y,
          left: x,
          width,
          height,
          borderRadius: 6,
          border: '2px solid rgba(99, 179, 237, 0.9)',
          boxShadow: '0 0 0 3px rgba(99, 179, 237, 0.18)',
          zIndex: 9999,
          pointerEvents: 'none',
          transition: TRANSITION,
        }}
      />
    </>
  );
};
